import type { Content, ContentHref, ContentRelation } from '@generalTypes/apiTypes';
import { ContentType } from '@generalTypes/apiTypes';
import type { RootState } from '@generalTypes/rootStateTypes';
import { getRelationsToAndFromMap } from '@newStore/documentApi/documentApiHelpers';
import {
  selectApiWithPendingChanges,
  selectSnapshotChangedHrefs,
} from '@newStore/documentApi/documentApiSelectors';
import { getAllDescendantHrefs } from '@newStore/documentUI/documentUIHelpers';
import { selectDocumentRootType } from '@newStore/documentUI/nodeTypeConfigSelectors';
import {
  getKrcExtendedIdentifierMap,
  selectKrcExtendedIdentifierMap,
} from '@newStore/documentUI/transformContent/krcContentNodeSelectors';
import { selectNodeDiffText } from '@newStore/documentUI/transformProposal/proposalSelectors';
import { krcCurriculumRoot } from '@newStore/referenceFrames/referenceFrames';
import { NodeType } from '@nodeTypeConfig/configTypes';
import { getResourceKey } from '@store/helpers/documentHelpers';
import { escape as escapeHtml, isEqual, omit } from 'lodash';
import { stripHtml } from '../genericHelpers';
import type { Snapshot, SnapshotContent } from './documentSnapshotTypes';

type SnapshotChangelogEntries = {
  additions: string[];
  modifications: string[];
  deletions: string[];
};

type SnapshotChangelogTreeEntry = {
  href: ContentHref;
  text: string;
};

const SNAPSHOT_CHANGELOG_NON_COMPARABLE_FIELDS = [
  '$$meta',
  '$$new',
  '$$version',
  'created',
  'modified',
];

const KRC_REFERENCE_FRAME_TYPES = new Set<ContentType>([
  ContentType.KRC_REFERENCE_FRAME,
  ContentType.KRC_GOAL_SET,
  ContentType.KRC_AGE_RANGE,
]);

// Structural KRC nodes that are hidden in the changes modal while their children are kept.
const KRC_FLATTENED_MODAL_TYPES = new Set<ContentType>([
  ContentType.KRC_GOAL_SET_ITEM,
  ContentType.KRC_AGE_RANGE_ITEM,
]);

export const shouldIncludeInSnapshotChangelog = (content: Content): boolean =>
  !KRC_REFERENCE_FRAME_TYPES.has(content.type);

export const hasSnapshotContentChanged = (
  currentContent: Content,
  snapshotContent: Content,
): boolean =>
  !isEqual(
    omit(currentContent, SNAPSHOT_CHANGELOG_NON_COMPARABLE_FIELDS),
    omit(snapshotContent, SNAPSHOT_CHANGELOG_NON_COMPARABLE_FIELDS),
  );

const getOrderedDocumentHrefs = (
  rootHref: ContentHref,
  content: Record<ContentHref, Content>,
  relations: ContentRelation[],
): ContentHref[] => {
  const rootItem = content[rootHref];
  if (!rootItem || rootItem.type === ContentType.KRC_REFERENCE_FRAME) {
    return [];
  }
  // Only disciplines join the document tree via IS_INCLUDED_IN; everything else uses IS_PART_OF.
  const documentRelations = relations.filter(
    (relation) =>
      relation.relationtype === 'IS_PART_OF' ||
      (relation.relationtype === 'IS_INCLUDED_IN' &&
        content[relation.from.href]?.type === ContentType.KRC_CURRICULUM_DISCIPLINE),
  );
  // Prune reference-frame subtrees so their goal-set/age-range items never leak into the changelog.
  const referenceFrameHrefs = new Set(
    (Object.keys(content) as ContentHref[]).filter(
      (href) => content[href]?.type === ContentType.KRC_REFERENCE_FRAME,
    ),
  );
  const toRelationsMap = getRelationsToAndFromMap(content, documentRelations).to;
  return [rootHref, ...getAllDescendantHrefs(toRelationsMap, rootHref, referenceFrameHrefs)];
};

export const getOrderedSnapshotChangelogHrefs = (
  rootHref: ContentHref,
  currentContent: Record<ContentHref, Content>,
  currentRelations: ContentRelation[],
  snapshotContent: Record<ContentHref, Content>,
  snapshotRelations: ContentRelation[],
): ContentHref[] => {
  const currentHrefs = getOrderedDocumentHrefs(rootHref, currentContent, currentRelations).filter(
    (href) => !currentContent[href]?.$$meta.deleted,
  );
  const currentHrefSet = new Set(currentHrefs);
  const deletedHrefs = getOrderedDocumentHrefs(rootHref, snapshotContent, snapshotRelations).filter(
    (href) => !currentHrefSet.has(href),
  );
  return [...currentHrefs, ...deletedHrefs];
};

export const formatKrcChangelogItem = (content: Content, identifier: string): string => {
  const title = stripHtml(content.title ?? '').trim();
  const displayText = (() => {
    switch (content.type) {
      case ContentType.KRC_CURRICULUM_DOMAIN:
      case ContentType.KRC_CURRICULUM_DISCIPLINE:
      case ContentType.KRC_CURRICULUM_GOAL:
        return identifier && title ? `${identifier} - ${title}` : identifier || title;
      case ContentType.KRC_GOAL_SET_ITEM:
      case ContentType.KRC_AGE_RANGE_ITEM:
        return [identifier, title].filter(Boolean).join(' ');
      default:
        return title || identifier;
    }
  })();
  return escapeHtml(displayText);
};

export const formatGoalDiffText = (content: Content, identifier: string): string =>
  `<li>${formatKrcChangelogItem(content, identifier)}</li>`;

export const formatSnapshotChangelogTree = (
  entries: SnapshotChangelogTreeEntry[],
  relations: ContentRelation[],
  flattenedHrefs?: Set<ContentHref>,
): string[] => {
  const entriesByHref = new Map<string, SnapshotChangelogTreeEntry>(
    entries.map((entry) => [entry.href, entry]),
  );
  // Precompute each node's parent-child parents once so nesting is a map lookup, not an O(R) scan per entry.
  const parentHrefsByChild = new Map<string, ContentHref[]>();
  relations.forEach((relation) => {
    if (relation.relationtype === 'IS_PART_OF' || relation.relationtype === 'IS_INCLUDED_IN') {
      const parents = parentHrefsByChild.get(relation.from.href) ?? [];
      parents.push(relation.to.href);
      parentHrefsByChild.set(relation.from.href, parents);
    }
  });
  const childrenByParent = new Map<string, SnapshotChangelogTreeEntry[]>();
  const roots: SnapshotChangelogTreeEntry[] = [];

  entries.forEach((entry) => {
    const parentHref = parentHrefsByChild
      .get(entry.href)
      ?.find((candidate) => entriesByHref.has(candidate));
    if (!parentHref) {
      roots.push(entry);
      return;
    }
    const children = childrenByParent.get(parentHref) ?? [];
    children.push(entry);
    childrenByParent.set(parentHref, children);
  });

  const renderEntry = (entry: SnapshotChangelogTreeEntry): string => {
    const children = childrenByParent.get(entry.href) ?? [];
    const childrenHtml = children.map(renderEntry).join('');

    // Flattened nodes are hidden, but their children bubble up to the parent level.
    if (flattenedHrefs?.has(entry.href)) {
      return childrenHtml;
    }
    if (children.length === 0) {
      return `<li>${entry.text}</li>`;
    }
    // Parent nodes get a caret so the reader can collapse their child subtree in the modal.
    const nestedChildren = `<ul class="snapshot-change-children">${childrenHtml}</ul>`;
    return `<li class="has-children"><span class="snapshot-change-caret"></span>${entry.text}${nestedChildren}</li>`;
  };

  return roots.map(renderEntry);
};

export const selectLatestSnapshot = (snapshots: Snapshot[]): Snapshot | undefined =>
  snapshots.reduce<Snapshot | undefined>((latest, snapshot) => {
    if (!latest) {
      return snapshot;
    }
    return Date.parse(snapshot.timestamp) > Date.parse(latest.timestamp) ? snapshot : latest;
  }, undefined);

export const formatSnapshotChangelog = ({
  additions,
  modifications,
  deletions,
}: SnapshotChangelogEntries): string => {
  const sections = [
    { title: 'TOEGEVOEGD', entries: additions },
    { title: 'GEWIJZIGD', entries: modifications },
    { title: 'VERWIJDERD', entries: deletions },
  ];
  const changelog = sections
    .filter(({ entries }) => entries.length > 0)
    .map(
      ({ title, entries }) =>
        `<p class="snapshot-change-section"><strong>${title}</strong></p><ul>${entries.join('')}</ul>`,
    )
    .join('');

  return changelog || '<p>Geen wijzigingen ten opzichte van de vorige versie.</p>';
};

const wrapFieldDiff = (diffHtml: string): string =>
  diffHtml ? `<ul class="snapshot-change-fields">${diffHtml}</ul>` : '';

// Resolves the field-level diff HTML (green additions / red strikethrough deletions) for a node via
// the same selectNodeDiffText the proposal indicators use. The diff baseline (last published version
// vs. the selected snapshot) is decided by the state passed in, not here.
const getNodeFieldDiff = (
  state: RootState,
  href: ContentHref,
  fromRelations: ContentRelation[] | undefined,
): string => {
  const parentHref =
    fromRelations?.find(
      (relation) =>
        relation.relationtype === 'IS_PART_OF' || relation.relationtype === 'IS_INCLUDED_IN',
    )?.to.href ?? null;
  const fieldDiff = selectNodeDiffText(state, href, parentHref);
  return typeof fieldDiff === 'string' ? fieldDiff : '';
};

// Shared walk behind both the publish changelog and the "show version changes" modal. Both classify
// every document node into added / modified / deleted trees; the only difference is includeFieldChanges:
// false for the publish changelog (identifier + title only), true for the modal (adds the green/red
// field-level diff and aside links). The publish changelog compares against the latest published
// snapshot, which is loaded into the store while the publish modal is open.
export const buildSnapshotChanges = (state: RootState, includeFieldChanges: boolean): string => {
  const apiWithPendingChanges = selectApiWithPendingChanges(state);
  const rootHref = state.documentUI.currentDocument;
  if (!rootHref) {
    return formatSnapshotChangelog({ additions: [], modifications: [], deletions: [] });
  }
  const documentKey = getResourceKey(rootHref);
  const currentIdentifierMap = selectKrcExtendedIdentifierMap(state);
  const snapshotRelations = Object.values(state.documentSnapshot.snapshotRelations);
  const snapshotIdentifierMap = getKrcExtendedIdentifierMap(
    getRelationsToAndFromMap(state.documentSnapshot.snapshotContent, snapshotRelations),
    state.documentSnapshot.snapshotContent,
  );
  const documentHrefs = getOrderedSnapshotChangelogHrefs(
    rootHref,
    apiWithPendingChanges.content,
    apiWithPendingChanges.relations,
    state.documentSnapshot.snapshotContent,
    snapshotRelations,
  );
  // Reuse the same changed-href set that drives the green-line / TOC indicators so this list and the
  // diff indicators detect exactly the same modifications.
  const changedHrefs = selectSnapshotChangedHrefs(state);

  const additions: SnapshotChangelogTreeEntry[] = [];
  const modifications: SnapshotChangelogTreeEntry[] = [];
  const deletions: SnapshotChangelogTreeEntry[] = [];
  // Hrefs hidden in the modal tree while their children are promoted to the parent level.
  const flattenedHrefs = new Set<ContentHref>();

  documentHrefs.forEach((href) => {
    const contentItem = apiWithPendingChanges.content[href];
    const snapshotContent = state.documentSnapshot.snapshotContent[href];
    const comparableContent = contentItem ?? snapshotContent;
    if (!comparableContent || !shouldIncludeInSnapshotChangelog(comparableContent)) {
      return;
    }
    if (includeFieldChanges && KRC_FLATTENED_MODAL_TYPES.has(comparableContent.type)) {
      flattenedHrefs.add(href);
    }
    const goalIdentifier =
      currentIdentifierMap.get(href) ??
      snapshotIdentifierMap.get(href) ??
      comparableContent.identifiers?.[0]?.trim() ??
      '';
    const label = formatKrcChangelogItem(comparableContent, goalIdentifier);

    // Deleted nodes no longer exist in the current state, so they are not field-diffed nor linked.
    if (!contentItem || contentItem.$$meta.deleted) {
      deletions.push({ href, text: includeFieldChanges ? `<del>${label}</del>` : label });
      return;
    }

    const isAddition = !snapshotContent;
    // Skip unchanged existing nodes before the costly field diff so it only runs for real changes.
    if (!isAddition && !changedHrefs.has(href)) {
      return;
    }

    // In the interactive modal the node header links to its aside; the plain changelog stays text.
    const header = includeFieldChanges
      ? `<a class="snapshot-change-link" href="/edit/${documentKey}/${getResourceKey(href)}">${label}</a>`
      : label;
    const fieldChanges = includeFieldChanges
      ? wrapFieldDiff(
          getNodeFieldDiff(state, href, apiWithPendingChanges.relationsToAndFromMap.from[href]),
        )
      : '';

    if (isAddition) {
      additions.push({ href, text: `${header}${fieldChanges}` });
      return;
    }
    modifications.push({ href, text: `${header}${fieldChanges}` });
  });

  return formatSnapshotChangelog({
    additions: formatSnapshotChangelogTree(
      additions,
      apiWithPendingChanges.relations,
      flattenedHrefs,
    ),
    modifications: formatSnapshotChangelogTree(
      modifications,
      apiWithPendingChanges.relations,
      flattenedHrefs,
    ),
    deletions: formatSnapshotChangelogTree(deletions, snapshotRelations, flattenedHrefs),
  });
};

export const selectSnapshotContext = (state: RootState) => {
  const currentDocumentHref = state.documentUI.currentDocument;
  const rootType = selectDocumentRootType(state);
  return {
    currentDocumentHref,
    rootType,
    snapshotDocumentHref:
      rootType === NodeType.KRC_CURRICULUM_DISCIPLINE ? krcCurriculumRoot : currentDocumentHref,
  };
};

export const scopeSnapshotToDiscipline = (
  snapshot: SnapshotContent,
  disciplineHref: ContentHref,
): SnapshotContent => {
  const contentMap = Object.fromEntries(
    snapshot.content.map((item) => [item.$$meta.permalink, item]),
  ) as Record<ContentHref, Content>;
  if (!contentMap[disciplineHref]) {
    return { content: [], relations: [] };
  }

  const scopedHrefs = new Set<string>([disciplineHref]);
  let addedDescendant = true;
  while (addedDescendant) {
    addedDescendant = false;
    snapshot.relations.forEach((relation) => {
      if (
        (relation.relationtype === 'IS_PART_OF' || relation.relationtype === 'IS_INCLUDED_IN') &&
        scopedHrefs.has(relation.to.href) &&
        contentMap[relation.from.href] &&
        !scopedHrefs.has(relation.from.href)
      ) {
        scopedHrefs.add(relation.from.href);
        addedDescendant = true;
      }
    });
  }

  const content = snapshot.content.filter((item) => scopedHrefs.has(item.$$meta.permalink));
  const relations = snapshot.relations.filter((relation) => {
    const isParentChild =
      relation.relationtype === 'IS_PART_OF' || relation.relationtype === 'IS_INCLUDED_IN';
    if (isParentChild) {
      return scopedHrefs.has(relation.from.href) && scopedHrefs.has(relation.to.href);
    }
    return scopedHrefs.has(relation.from.href) || scopedHrefs.has(relation.to.href);
  });

  return { content, relations };
};
